/**
 * Kiosk device authentication lifecycle.
 *
 * Token layout in secureStorage:
 *   access_token            15-min JWT
 *   access_token_exp        ISO expiry (UTC)
 *   refresh_token           90-day JWT (never logged)
 *   refresh_token_exp       ISO expiry (UTC)
 *   device_id               UUID from /devices/claim
 *   location_id             UUID from /devices/claim
 *   re_key_required_at      ISO timestamp from X-Re-Key-Required-At header
 *
 * Auto-refresh: every 14 minutes (1 min before access token TTL of 15 min).
 * Heartbeat: every 5 minutes.
 */

import { kioskClient } from "./kiosk-client";
import { secureStorage } from "./secure-storage";

const ACCESS_REFRESH_INTERVAL_MS = 14 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

let refreshTimer: ReturnType<typeof setInterval> | null = null;
let heartbeatTimer: ReturnType<typeof setInterval> | null = null;

export function isProvisioned(): boolean {
  return (
    secureStorage.getItem("access_token") !== null &&
    secureStorage.getItem("refresh_token") !== null
  );
}

export function isAccessTokenValid(): boolean {
  const exp = secureStorage.getItem("access_token_exp");
  if (!exp) return false;
  return new Date(exp) > new Date();
}

export function isRefreshTokenValid(): boolean {
  const exp = secureStorage.getItem("refresh_token_exp");
  if (!exp) return false;
  return new Date(exp) > new Date();
}

export function getDeviceId(): string | null {
  return secureStorage.getItem("device_id");
}

export function getLocationId(): string | null {
  return secureStorage.getItem("location_id");
}

export function getReKeyRequiredAt(): string | null {
  return secureStorage.getItem("re_key_required_at");
}

export function isReKeyWindowActive(): boolean {
  const reKeyAt = getReKeyRequiredAt();
  if (!reKeyAt) return false;
  return new Date() >= new Date(reKeyAt);
}

function storeTokens(
  accessToken: string,
  accessExpiresIn: number,
  refreshToken: string,
  refreshExpiresAt: string,
  deviceId?: string,
  locationId?: string,
  reKeyRequiredAt?: string | null,
): void {
  const accessExp = new Date(Date.now() + accessExpiresIn * 1000).toISOString();
  secureStorage.setItem("access_token", accessToken);
  secureStorage.setItem("access_token_exp", accessExp);
  secureStorage.setItem("refresh_token", refreshToken);
  secureStorage.setItem("refresh_token_exp", refreshExpiresAt);
  if (deviceId) secureStorage.setItem("device_id", deviceId);
  if (locationId) secureStorage.setItem("location_id", locationId);
  if (reKeyRequiredAt !== undefined) {
    if (reKeyRequiredAt) {
      secureStorage.setItem("re_key_required_at", reKeyRequiredAt);
    } else {
      secureStorage.removeItem("re_key_required_at");
    }
  }
}

export async function claimDevice(provisionCode: string): Promise<void> {
  const { data, reKeyRequiredAt } =
    await kioskClient.claimDevice(provisionCode);
  storeTokens(
    data.access_token,
    data.access_token_expires_in,
    data.refresh_token,
    data.refresh_token_expires_at,
    data.device_id,
    data.location_id,
    reKeyRequiredAt,
  );
  startBackgroundTasks();
}

export async function refreshAccessToken(): Promise<void> {
  const refreshToken = secureStorage.getItem("refresh_token");
  if (!refreshToken) throw new Error("No refresh token");
  const { data, reKeyRequiredAt } =
    await kioskClient.refreshTokens(refreshToken);
  storeTokens(
    data.access_token,
    data.access_token_expires_in,
    data.refresh_token,
    data.refresh_token_expires_at,
    undefined,
    undefined,
    reKeyRequiredAt,
  );
}

export function clearCredentials(): void {
  stopBackgroundTasks();
  secureStorage.clear();
}

export function startBackgroundTasks(): void {
  stopBackgroundTasks();
  refreshTimer = setInterval(async () => {
    try {
      await refreshAccessToken();
    } catch {
      // On refresh failure the next API call will get 401 and the app
      // will route to SetupPage via the error boundary.
    }
  }, ACCESS_REFRESH_INTERVAL_MS);

  heartbeatTimer = setInterval(async () => {
    try {
      const { reKeyRequiredAt } = await kioskClient.heartbeat();
      if (reKeyRequiredAt !== null) {
        secureStorage.setItem("re_key_required_at", reKeyRequiredAt);
      }
    } catch {
      // Heartbeat failures are non-fatal; the next refresh will catch revocation.
    }
  }, HEARTBEAT_INTERVAL_MS);
}

export function stopBackgroundTasks(): void {
  if (refreshTimer !== null) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
  if (heartbeatTimer !== null) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}
